import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mesa, MesaForma } from '@/types';

interface MesaEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mesa: Mesa | null;
  onSave: (patch: { nombre: string | null; capacidad: number; forma: MesaForma }) => Promise<void> | void;
}

export const MesaEditModal: React.FC<MesaEditModalProps> = ({ open, onOpenChange, mesa, onSave }) => {
  const [nombre, setNombre] = useState('');
  const [capacidad, setCapacidad] = useState(10);
  const [forma, setForma] = useState<MesaForma>('REDONDA');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mesa) {
      setNombre(mesa.nombre ?? '');
      setCapacidad(mesa.capacidad);
      setForma(mesa.forma);
    }
  }, [mesa]);

  const handleSave = async () => {
    if (!mesa) return;
    const trimmed = nombre.trim();
    setSubmitting(true);
    try {
      await onSave({
        nombre: trimmed.length === 0 ? null : trimmed,
        capacidad,
        forma,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Mesa {mesa?.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="mesa-nombre">Nombre (opcional)</Label>
            <Input
              id="mesa-nombre"
              placeholder="Ej: Familia novia"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={60}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="mesa-capacidad">Capacidad</Label>
              <Input
                id="mesa-capacidad"
                type="number"
                min={1}
                max={50}
                value={capacidad}
                onChange={(e) => setCapacidad(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mesa-forma">Forma</Label>
              <Select value={forma} onValueChange={(v) => setForma(v as MesaForma)}>
                <SelectTrigger id="mesa-forma">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REDONDA">Redonda</SelectItem>
                  <SelectItem value="CUADRADA">Cuadrada</SelectItem>
                  <SelectItem value="RECTANGULAR">Rectangular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={submitting}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
